import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Notification from '../components/Admin/Notification';

const { FiCheck, FiArrowRight } = FiIcons;

const Pricing = () => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(null);
  const [notify, setNotify] = useState(null);
  const navigate = useNavigate();

  // Checkout is not wired to a payment provider yet. The previous version only simulated
  // a Stripe purchase by letting the browser flip its own account to Pro, which the server
  // no longer allows. Real checkout comes from NoCodeBackend billing (Account > Billing).
  const handleSubscribe = async () => {
    if (!user) return navigate('/login');
    setNotify({
      message: 'Online checkout is not available yet. Billing is being moved to the new account system.',
      type: 'error'
    });
  };

  return (
    <div className="max-w-4xl mx-auto py-8">
      {notify && <Notification {...notify} onClose={() => setNotify(null)} />}
      <div className="mb-8 text-left">
        <div className="caption mb-2">Plans and pricing</div>
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Unlimited access to the master parts database</h1>
        <p className="text-slate-600 dark:text-slate-400 max-w-2xl mt-2">Both plans include every search, specification and cross-reference. Choose how you prefer to be billed.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        <div className="panel p-8 flex flex-col text-left">
          <div className="caption mb-3">Monthly</div>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-4xl font-semibold font-mono text-slate-900 dark:text-white">$29</span>
            <span className="text-sm text-slate-500">per month</span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Month-to-month billing for individual technicians.</p>
          <div className="space-y-3 mb-8 flex-1 border-t border-slate-200 dark:border-slate-800 pt-5">
            <Feature icon={FiCheck} text="Unlimited database searches" />
            <Feature icon={FiCheck} text="Full technical specifications" />
            <Feature icon={FiCheck} text="Priority NLA review queue" />
            <Feature icon={FiCheck} text="Cancel anytime, no contracts" />
          </div>
          <button onClick={() => handleSubscribe('monthly')} disabled={loading} className="btn-secondary w-full">
            {loading === 'monthly' ? 'Processing…' : 'Subscribe monthly'}
          </button>
        </div>

        <div className="panel p-8 flex flex-col text-left border-2 !border-blue-600 relative">
          <div className="absolute -top-px right-6 px-3 py-1 bg-blue-600 text-white font-cond text-[11px] font-semibold uppercase tracking-wider">
            Save 20%
          </div>
          <div className="caption mb-3 !text-blue-700 dark:!text-blue-300">Annual</div>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-4xl font-semibold font-mono text-slate-900 dark:text-white">$279</span>
            <span className="text-sm text-slate-500">per year</span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">One annual invoice for shops and repair teams.</p>
          <div className="space-y-3 mb-8 flex-1 border-t border-slate-200 dark:border-slate-800 pt-5">
            <Feature icon={FiCheck} text="Unlimited database searches" />
            <Feature icon={FiCheck} text="Advanced technical data" />
            <Feature icon={FiCheck} text="Historical NLA audit logs" />
            <Feature icon={FiCheck} text="Bulk master data exports" />
            <Feature icon={FiCheck} text="Dedicated support channel" />
          </div>
          <button onClick={() => handleSubscribe('annual')} disabled={loading} className="btn-primary w-full">
            {loading === 'annual' ? 'Processing…' : <>Subscribe annually <SafeIcon icon={FiArrowRight} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ icon, text }) => (
  <div className="flex items-center gap-3">
    <SafeIcon icon={icon} className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-400" />
    <span className="text-sm text-slate-700 dark:text-slate-300">{text}</span>
  </div>
);

export default Pricing;
